/**
 * Hook for managing the user's travel trip list.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import { queryKeys } from '@/lib/query-keys';
import type { TravelTripResponse } from '../api';

export function useTravelTrips() {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.travel.my();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => travelApi.getMyTravelTrips(),
  });

  const createMut = useMutation({
    mutationFn: (input: { title?: string; startDate?: string }) =>
      travelApi.createTravelTrip(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (tripId: string) => travelApi.deleteTravelTrip(tripId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
    },
  });

  return {
    trips: data?.trips ?? ([] as TravelTripResponse[]),
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refresh: refetch,
    createTrip: (input: { title?: string; startDate?: string }) => createMut.mutateAsync(input),
    deleteTrip: (tripId: string) => deleteMut.mutateAsync(tripId),
    creating: createMut.isPending,
  };
}
